import { AvatarFile, LocalizedText, SocialLinkType, VoiceStatus } from 'shared/api';
import { getLanguageName } from 'shared/config';

export const getSocialLinkTypeLabel = (type: SocialLinkType): string => {
  const labels: Record<SocialLinkType, string> = {
    [SocialLinkType.Website]: 'Website',
    [SocialLinkType.Facebook]: 'Facebook',
    [SocialLinkType.Youtube]: 'YouTube',
    [SocialLinkType.Twitter]: 'Twitter',
    [SocialLinkType.Instagram]: 'Instagram',
    [SocialLinkType.Tiktok]: 'TikTok',
    [SocialLinkType.Telegram]: 'Telegram',
    [SocialLinkType.Threads]: 'Threads',
  };

  return labels[type] ?? type;
};

export const VOICE_STATUS_LABELS: Record<VoiceStatus, string> = {
  [VoiceStatus.NotContacted]: 'Not contacted',
  [VoiceStatus.InDiscussion]: 'In discussion',
  [VoiceStatus.Posted]: 'Posted',
  [VoiceStatus.Declined]: 'Declined',
};

export const VOICE_STATUS_COLORS: Record<VoiceStatus, 'default' | 'info' | 'success' | 'error'> = {
  [VoiceStatus.NotContacted]: 'default',
  [VoiceStatus.InDiscussion]: 'info',
  [VoiceStatus.Posted]: 'success',
  [VoiceStatus.Declined]: 'error',
};

/**
 * У документов, заведённых до появления поля, status нет — показываем как schema default.
 * Но серверный фильтр status=not_contacted точный и таких документов до бэкфилла не находит.
 */
export const getVoiceStatus = (status?: VoiceStatus): VoiceStatus => status ?? VoiceStatus.NotContacted;

export const isVoiceStatus = (value: string | null): value is VoiceStatus =>
  !!value && (Object.values(VoiceStatus) as string[]).includes(value);

/** Точное значение для языка, без фолбэка. '' если записи нет. */
export const getLocalizedText = (items: LocalizedText[] | undefined, lang: string): string =>
  items?.find((item) => item.language === lang)?.text ?? '';

/**
 * Фолбэк lang → en → ru → ''. Повторяет src/voices/utils/resolve-localized.ts на бэке —
 * в проекте одна модель фолбэка, второй не заводим.
 */
export const resolveLocalized = (items: LocalizedText[] | undefined, lang: string): string => {
  if (!items?.length) return '';

  for (const candidate of [lang, 'en', 'ru']) {
    const found = items.find((item) => item.language === candidate);
    if (found?.text) return found.text;
  }

  return '';
};

/** Коды языков с непустым текстом — для чипов в таблице. */
export const getFilledLanguages = (items: LocalizedText[] | undefined): string[] =>
  items?.filter((item) => item.text.trim()).map((item) => item.language) ?? [];

/** Иммутабельно проставляет текст для языка, создавая запись при необходимости. */
export const setLocalizedText = (items: LocalizedText[] | undefined, lang: string, text: string): LocalizedText[] => {
  const list = items ?? [];
  return list.some((item) => item.language === lang)
    ? list.map((item) => (item.language === lang ? { ...item, text } : item))
    : [...list, { language: lang, text }];
};

export const removeLocalizedLanguage = (items: LocalizedText[] | undefined, lang: string): LocalizedText[] =>
  (items ?? []).filter((item) => item.language !== lang);

/**
 * Языки, у которых заявлен таб, но не заполнен Bio.
 * Используется и в Yup-тесте, и для красных точек на табах — источник один.
 */
export const getMissingBioLanguages = (languages: string[], bio: LocalizedText[] | undefined): string[] =>
  languages.filter((lang) => !getLocalizedText(bio, lang).trim());

export const formatMissingBioLanguages = (languages: string[]): string => languages.map(getLanguageName).join(', ');

/** avatar приходит либо id строкой (после создания), либо объектом файла (из списка/byId). */
export const isAvatarFile = (avatar: string | AvatarFile | undefined | null): avatar is AvatarFile =>
  typeof avatar === 'object' && avatar !== null && 'path' in avatar;

/** Путь файла аватара для загрузки через /files/doc, undefined если файла нет. */
export const getAvatarPath = (avatar: string | AvatarFile | undefined | null): string | undefined =>
  isAvatarFile(avatar) ? avatar.path : undefined;
